import * as Yup from "yup";

const countrySchema = Yup.object({
  code: Yup.string().required("Country code is required"),
  sortOrder: Yup.number().required("Country sort order is required"),
  name: Yup.string().required("Country name is required"),
  name_si: Yup.string().required("Sinhala country name is required"),
  currency: Yup.string().required("Currency is required"),
  currencySymbol: Yup.string().required("Currency symbol is required"),
});

const religionSchema = Yup.object({
  id: Yup.string().required("Religion ID is required"),
  name: Yup.string().required("Religion name is required"),
  name_si: Yup.string().required("Sinhala religion name is required"),
  sortOrder: Yup.number().required("Religion sort order is required"),
});

const ethnicitySchema = Yup.object({
  id: Yup.string().required("Ethnicity ID is required"),
  name: Yup.string().required("Ethnicity name is required"),
  name_si: Yup.string().required("Sinhala ethnicity name is required"),
  sortOrder: Yup.number().required("Ethnicity sort order is required"),
});

const professionSchema = Yup.object({
  id: Yup.number().required("Profession ID is required"),
  sortOrder: Yup.number().required("Profession sort order is required"),
  name: Yup.string().required("Profession is required"),
  name_si: Yup.string().required("Sinhala profession is required"),
});

const residentRegionSchema = Yup.object({
  id: Yup.number().required("Resident region ID is required"),
  countryCode: Yup.string().required(
    "Resident region country code is required",
  ),
  regionName: Yup.string().required("Resident region name is required"),
  regionName_si: Yup.string().required(
    "Sinhala resident region name is required",
  ),
});

const educationLevelSchema = Yup.object({
  id: Yup.number().required("Education level ID is required"),
  sortOrder: Yup.number().required("Education level sort order is required"),
  level: Yup.string().required("Education level is required"),
  level_si: Yup.string().required("Sinhala education level is required"),
  value: Yup.number().required("Education level value is required"),
});

const civilStatusSchema = Yup.object({
  id: Yup.string().required("Civil status ID is required"),
  status: Yup.string().required("Civil status is required"),
  status_si: Yup.string().required("Sinhala civil status is required"),
  sortOrder: Yup.number().required("Civil status sort order is required"),
});

const foodPreferenceSchema = Yup.object({
  id: Yup.string().required("Food preference ID is required"),
  type: Yup.string().required("Food preference is required"),
  type_si: Yup.string().required("Sinhala food preference is required"),
  sortOrder: Yup.number().required("Food preference sort order is required"),
});

const visaTypeSchema = Yup.object({
  id: Yup.string().required("Visa type ID is required"),
  visaType: Yup.string().required("Visa type is required"),
  visaType_si: Yup.string().required("Sinhala visa type is required"),
  sortOrder: Yup.number().required("Visa type sort order is required"),
});

const personalInfoSchema = Yup.object({
  displayName: Yup.string().required("Display name is required"),

  residentCountry: countrySchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Resident country is required"),

  residentRegion: residentRegionSchema.nullable().transform((value) => {
    if (!value || Object.keys(value).length === 0) {
      return null;
    }
    return value;
  }),

  residentCity: Yup.string().required("Resident city is required"),

  age: Yup.number()
    .required("Age is required")
    .min(18, "Age must be at least 18")
    .max(60, "Age must be at most 60"),

  dob: Yup.string().required("Date of birth is required"),

  gender: Yup.string().required("Gender is required"),

  height: Yup.number().required("Height is required"),

  religion: religionSchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Religion is required"),

  ethnicity: ethnicitySchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Ethnicity is required"),

  caste: Yup.string().nullable(),

  profession: professionSchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Profession is required"),

  professionOther: Yup.string().nullable(),

  civilStatus: civilStatusSchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Civil status is required"),

  originCountry: countrySchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Birth country is required"),

  educationLevel: educationLevelSchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Education level is required"),

  drinking: Yup.string().required("Drinking preference is required"),

  smoking: Yup.string().required("Smoking preference is required"),

  foodPreference: foodPreferenceSchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Food preference is required"),

  additionalInfo: Yup.string().nullable(),

  visaType: visaTypeSchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Visa type is required"),

  displayNameFormat: Yup.string().required(
    "Display name format is required",
  ),

  fname: Yup.string().required("First name is required"),

  lname: Yup.string().required("Last name is required"),
});

const parentInfoSchema = Yup.object({
  id: Yup.string().required("Parent ID is required"),

  type: Yup.string().required("Parent type is required"),

  residentCountry: countrySchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Resident country is required"),

  religion: religionSchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Religion is required"),

  ethnicity: ethnicitySchema
    .nullable()
    .transform((value) => {
      if (!value || Object.keys(value).length === 0) {
        return null;
      }

      return value;
    })
    .required("Ethnicity is required"),

  additionalInfo: Yup.string().nullable(),

  caste: Yup.string().nullable(),

  profession: professionSchema
    .nullable()
    .default(null)
    .notRequired(),

  professionOther: Yup.string().nullable(),
});

export const postValidationSchema = Yup.object({
  id: Yup.string().required("Post ID is required"),

  personalInfo: personalInfoSchema.required(
    "Personal information is required",
  ),

  parentInfo: Yup.array()
    .of(parentInfoSchema)
    .required("Parent information is required")
    .min(2, "At least two parent information records are required"),

  status: Yup.string().required("Status is required"),

  memberId: Yup.string().required("Member ID is required"),
});